import React, { useState, useContext, useEffect } from 'react';
import { LogoLoading } from 'ensightful-koala-ui';
import moment from 'moment';

import withResponsiveLayout from 'hoc/withResponsive';
import { useViewport } from 'contexts/viewport';
import { PageContext } from 'pages/PeerEvaluation/PageProvider';
import Mobile from 'pages/PeerEvaluation/mobile';
import Tablet from 'pages/PeerEvaluation/tablet';
import Desktop from 'pages/PeerEvaluation/desktop';
import {
    CreatePeerEvaluation,
    FilterSortPeerEvaluations,
    PeerEvaluationTable,
    StudentEvaluation,
} from 'components/organisms/PeerEvaluation';
import { AlertDialog, Drawer } from 'shell/components';
import ViewSubmissions from 'components/organisms/PeerEvaluation/ViewSubmissions';
import { useUser2 } from 'contexts/user2';

const countFilters = (filterState) => {
    let count = 0;
    if (filterState.sortKey !== 'titlea-z') count++;
    if (filterState.statusFilterKeys.length) count++;
    if (filterState.dueDateFilterKeys.length) count++;
    return count;
};

const getPeerEvaluationStatus = (template) => {
    const now = new Date().getTime();
    if (!template.published_on) return 'Saved as Draft';
    if (template.published_on < now) return 'Published';
    return `Scheduled for ${moment(template.schedule_on).format('MMMM D[,] YYYY')}`;
};

const PeerEvaluation = () => {
    const { width } = useViewport();
    const {
        auth,
        currentPeerEvaluationId,
        currentPeerEvaluation,
        submissions,
        instructorPage,
        dialog,
        setDialog,
        peerEvaluationList = [],
        courseLoading,
        handleStudentBackButton,

        INSTRUCTOR_PAGES,
    } = useContext(PageContext);
    const { isLoading, studentAccountUsers, groups } = useUser2();

    const [filterType, setFilterType] = useState('peerEvaluationTable');
    const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
    const [sortKey, setSortKey] = useState({ peerEvaluationTable: 'closestDue', submissionTable: 'lastNamea-z' });
    const [filterKey, setFilterKey] = useState({ status: [], dueDate: [], groups: [] });
    const [searchBarInput, setSearchBarInput] = useState('');

    const onChangeSearchBarInput = (value) => {
        if (value === '' || !value) setSearchBarInput('');
        else setSearchBarInput(value);
    };

    const handleFilterClick = (key, data) => {
        switch (key) {
            case 'SORT': {
                if (filterType === 'peerEvaluationTable') {
                    return setSortKey((prev) => ({ ...prev, peerEvaluationTable: data === sortKey ? '' : data }));
                }
                return setSortKey((prev) => ({ ...prev, submissionTable: data === sortKey ? '' : data }));
            }
            case 'STATUS': {
                const hasItem = filterKey.status.includes(data);
                let newData = filterKey.status;
                if (hasItem) {
                    newData = newData.filter((item) => item !== data);
                } else {
                    newData = [...filterKey.status, data];
                }
                return setFilterKey((prev) => ({ ...prev, status: newData }));
            }
            case 'DUEDATE': {
                const hasItem = filterKey.dueDate.includes(data);
                let newData = filterKey.dueDate;
                if (hasItem) {
                    newData = newData.filter((item) => item !== data);
                } else {
                    newData = [...filterKey.dueDate, data];
                }
                return setFilterKey((prev) => ({ ...prev, dueDate: newData }));
            }
            case 'GROUPS': {
                const hasItem = filterKey.groups.includes(data);
                let newData = filterKey.groups;
                if (hasItem) {
                    newData = newData.filter((item) => item !== data);
                } else {
                    newData = [...filterKey.groups, data];
                }
                return setFilterKey((prev) => ({ ...prev, groups: newData }));
            }
            default:
                return;
        }
    };

    const handleResetFilterSort = () => {
        setSortKey({ peerEvaluationTable: 'closestDue', submissionTable: 'lastNamea-z' });
        setFilterKey({ status: [], dueDate: [], groups: [] });
    };

    const getSortedFilteredPeerEvaluations = () => {
        return peerEvaluationList
            .filter((peerEvaluation) => {
                if (searchBarInput) {
                    return peerEvaluation.title.toLowerCase().includes(searchBarInput.toLowerCase());
                }
                return true;
            })
            .filter((peerEvaluation) => {
                if (filterKey.status.length === 0) return true;
                return filterKey.status.some((key) => {
                    return peerEvaluation.status.toLowerCase().includes(key);
                });
            })
            .filter((peerEvaluation) => {
                if (filterKey.dueDate.length === 0) return true;
                const today = moment(new Date()).hour(0).minute(0).second(0).millisecond(0);
                const dueDate = moment(peerEvaluation.due_on);
                if (filterKey.dueDate.includes('today')) {
                    return dueDate.format('D M YYYY') === today.format('D M YYYY');
                }
                if (filterKey.dueDate.includes('thisweek')) {
                    const startOfWeek = today.clone().day(0).add(1, 'day');
                    const endOfWeek = today.clone().day(6).add(1, 'day');
                    return dueDate.isSameOrAfter(startOfWeek) && dueDate.isSameOrBefore(endOfWeek);
                }
                if (filterKey.dueDate.includes('nextweek')) {
                    const startOfNextWeek = today.clone().add(7, 'days').day(0).add(1, 'day');
                    const endOfNextWeek = today.clone().add(7, 'days').day(6).add(1, 'day');
                    return dueDate.isSameOrAfter(startOfNextWeek) && dueDate.isSameOrBefore(endOfNextWeek);
                }
                if (filterKey.dueDate.includes('overdue')) {
                    return dueDate.toDate() < today.toDate();
                }
                return false;
            })
            .sort((a, b) => {
                const curSortKey = sortKey[filterType];
                if (curSortKey === 'titlea-z') {
                    return a.title.toLowerCase() > b.title.toLowerCase() ? 1 : -1;
                }
                if (curSortKey === 'titlez-a') {
                    return a.title.toLowerCase() < b.title.toLowerCase() ? 1 : -1;
                }
                if (curSortKey === 'closestDue') {
                    return a.end_on - b.end_on;
                }
                if (curSortKey === 'furthestDue') {
                    return b.end_on - a.end_on;
                }
                if (curSortKey === 'completed') {
                    if (getPeerEvaluationStatus(a) !== 'Published' && getPeerEvaluationStatus(b) !== 'Published')
                        return 0;
                    if (getPeerEvaluationStatus(a) !== 'Published') return 1;
                    if (getPeerEvaluationStatus(b) !== 'Published') return -1;
                }
                if (curSortKey === 'incomplete') {
                    if (getPeerEvaluationStatus(a) !== 'Published' && getPeerEvaluationStatus(b) !== 'Published')
                        return 0;
                    if (getPeerEvaluationStatus(a) !== 'Published') return 1;
                    if (getPeerEvaluationStatus(b) !== 'Published') return -1;
                }

                return 0;
            });
    };

    const getSortedFilteredMembers = () => {
        return studentAccountUsers
            .filter((student) => {
                const findGroup = groups.find((group) => group.id === student.group_id);
                if (searchBarInput) {
                    return (
                        student.first_name.toLowerCase().includes(searchBarInput.toLowerCase()) ||
                        student.last_name.toLowerCase().includes(searchBarInput.toLowerCase()) ||
                        findGroup?.name.toLowerCase().includes(searchBarInput.toLowerCase())
                    );
                }
                return true;
            })
            .filter((student) => {
                if (filterKey.groups.length === 0) return true;
                if (filterKey.groups.includes(student.group_id)) return true;
                return false;
            })
            .sort((a, b) => {
                const curSortKey = sortKey[filterType];
                let submissionA = submissions.find((submission) => submission.owner_account_id === a.owner_account_id);
                let submissionB = submissions.find((submission) => submission.owner_Bccount_id === b.owner_account_id);
                if (curSortKey === 'lastNamea-z') {
                    return a.last_name?.toLowerCase() > b.last_name?.toLowerCase() ? 1 : -1;
                }
                if (curSortKey === 'lastNamez-a') {
                    return b.last_name?.toLowerCase() > a.last_name?.toLowerCase() ? 1 : -1;
                }
                if (curSortKey === 'complete') {
                    return !submissionA ? 1 : -1;
                }
                if (curSortKey === 'incomplete') {
                    return submissionA ? 1 : -1;
                }
                if (curSortKey === 'submittedEarliest') {
                    return submissionA?.created_on || 0 > submissionB?.created_on || 0 ? -1 : 1;
                }
                if (curSortKey === 'submittedLatest') {
                    return submissionB?.created_on || 0 > submissionA?.created_on || 0 ? 1 : -1;
                }
                return 0;
            });
    };

    useEffect(() => {
        getSortedFilteredPeerEvaluations();
    }, [peerEvaluationList]);

    useEffect(() => {
        if (auth.role === 'instructor') {
            switch (instructorPage) {
                case INSTRUCTOR_PAGES.TABLE: {
                    onChangeSearchBarInput('');
                    return setFilterType('peerEvaluationTable');
                }
                case INSTRUCTOR_PAGES.SUBMISSIONS: {
                    onChangeSearchBarInput('');
                    return setFilterType('submissionTable');
                }
                default:
                    return;
            }
        } else {
            onChangeSearchBarInput('');
            setFilterType('peerEvaluationTable');
        }
    }, [instructorPage]);

    const renderPage = () => {
        if (auth.role === 'instructor') {
            switch (instructorPage) {
                case INSTRUCTOR_PAGES.EDIT:
                case INSTRUCTOR_PAGES.CREATE: {
                    return <CreatePeerEvaluation {...props} />;
                }
                case INSTRUCTOR_PAGES.TABLE: {
                    return <PeerEvaluationTable peerEvaluationList={getSortedFilteredPeerEvaluations()} {...props} />;
                }
                case INSTRUCTOR_PAGES.SUBMISSIONS: {
                    return <ViewSubmissions {...props} />;
                }
                default:
                    return;
            }
        } else {
            if (currentPeerEvaluationId && currentPeerEvaluation) {
                return <StudentEvaluation handleBack={handleStudentBackButton} {...props} />;
            }
            return <PeerEvaluationTable peerEvaluationList={getSortedFilteredPeerEvaluations()} {...props} />;
        }
    };

    const props = {
        getSortedFilteredPeerEvaluations,
        getSortedFilteredMembers,
        searchBarInput,
        onChangeSearchBarInput,
        renderPage,
        isFilterDrawerOpen,
        setIsFilterDrawerOpen,
    };

    const filterDrawerProps = {
        open: isFilterDrawerOpen,
        onClose: () => setIsFilterDrawerOpen(false),
    };

    const responsiveLayouts = {
        mobile: <Mobile {...props} />,
        tablet: <Tablet {...props} />,
        desktop: <Desktop {...props} />,
    };

    const FilterDrawerInner = (
        <FilterSortPeerEvaluations
            type={filterType}
            role={auth.role}
            sortKey={sortKey}
            filterKey={filterKey}
            onClick={handleFilterClick}
            onClose={() => setIsFilterDrawerOpen(false)}
            resetFilterSort={handleResetFilterSort}
        />
    );

    const responsiveFilterDrawers = {
        mobile: (
            <Drawer anchor={'left'} device={'mobile'} {...filterDrawerProps}>
                {FilterDrawerInner}
            </Drawer>
        ),
        tablet: (
            <Drawer anchor={'left'} device={'tablet'} {...filterDrawerProps}>
                {FilterDrawerInner}
            </Drawer>
        ),
        desktop: (
            <Drawer anchor={'left'} device={'tablet'} {...filterDrawerProps}>
                {FilterDrawerInner}
            </Drawer>
        ),
    };

    if (courseLoading || isLoading) return <LogoLoading />;

    return (
        <>
            {withResponsiveLayout(width, responsiveLayouts)}
            {withResponsiveLayout(width, responsiveFilterDrawers)}
            <AlertDialog
                message={dialog.message}
                description={dialog.description}
                open={dialog.open}
                variant={dialog.variant}
                actionButtonText={dialog.actionButtonText}
                onAction={dialog.onAction}
                onCancel={() => setDialog((current) => ({ ...current, open: false }))}
                onClose={() => setDialog((current) => ({ ...current, open: false }))}
            />
        </>
    );
};

export default PeerEvaluation;
